import { LevelName, WinType } from "../../abstracts/types/enums";
import { IWinLevel } from "../../abstracts/types/interfaces";
import { ApocalypseSoundNames } from "../assetsLoader/ApocalypseAssetsNames";
import { ApocalypseSymbolNames } from "../types/ApocalypseEnums";
import {
    AmbientSoundConfig,
    IApocalypseConfig,
    IApocalypseDevDebugSettings,
    IApocalypseDisplayConfig,
    IApocalypseReelAnimation,
    IApocalypseReelConfig,
    IApocalypseSymbolConfig,
} from "../types/ApocalypseInterfaces";

export const davidPanningReelStop: number[] = [-0.25, -0.18, 0.0, 0.18, 0.25];

//timeFrame (in seconds) is used to set the time at which the sound will be played.
//for example: when game starts, the first bird sound will be played at a random time between 20 and 30 seconds.
//once the sound event was triggered, the time counter will reset and the next sound will be played
//at other random time between 20 and 30 seconds.
export const ambientalSoundSettings: AmbientSoundConfig[] = [
    {
        name: `radiation`,
        timeFrame: [20, 30],
        soundsList: [
            ApocalypseSoundNames.AMBIENT_RADIATION_01,
            ApocalypseSoundNames.AMBIENT_RADIATION_02,
            ApocalypseSoundNames.AMBIENT_RADIATION_03,
            ApocalypseSoundNames.AMBIENT_RADIATION_04,
        ],
    },
    {
        name: `walkie`,
        timeFrame: [30, 50],
        soundsList: [
            ApocalypseSoundNames.AMBIENT_WALKIE_01,
            ApocalypseSoundNames.AMBIENT_WALKIE_02,
            ApocalypseSoundNames.AMBIENT_WALKIE_03,
            ApocalypseSoundNames.AMBIENT_WALKIE_04,
        ],
    },
];

export const winLevels: IWinLevel[] = [
    { name: LevelName.TINY, winType: WinType.TINY, min: 0, max: 1, animationDuration: 0.4 },
    { name: LevelName.SMALL, winType: WinType.SMALL, min: 1, max: 3, animationDuration: 0.53 },
    { name: LevelName.MEDIUM, winType: WinType.MEDIUM, min: 3, max: 5, animationDuration: 0.66 },
    { name: LevelName.LARGE, winType: WinType.LARGE, min: 5, max: 15, animationDuration: 0.8 },
    { name: LevelName.BIG_WIN_1, winType: WinType.BIG, min: 15, max: 25, animationDuration: 6 },
    { name: LevelName.BIG_WIN_2, winType: WinType.BIG, min: 25, max: 50, animationDuration: 6 },
    { name: LevelName.BIG_WIN_3, winType: WinType.BIG, min: 50, max: 100, animationDuration: 6 },
    { name: LevelName.BIG_WIN_4, winType: WinType.BIG, min: 100, max: 250, animationDuration: 6 },
    { name: LevelName.BIG_WIN_5, winType: WinType.BIG, min: 250, max: 20000, animationDuration: 6 },
    {
        name: LevelName.MAX_CAP,
        winType: WinType.MAX_CAP,
        min: 20000,
        max: 22000, //not being used
        animationDuration: 6,
    },
];

export const getWinLevel = (winMultiplier: number): IWinLevel => {
    const filteredWinLevels = winLevels.filter((level: IWinLevel) => level.min <= winMultiplier);

    return winMultiplier < 0 ? winLevels[0] : filteredWinLevels[filteredWinLevels.length - 1];
};

export const BASE_REELSET: string[][] = [
    [`L1`, `M5`, `L1`, `M5`],
    [`L3`, `L3`, `M2`, `L3`],
    [`M1`, `M4`, `M3`, `M1`],
    [`L2`, `L2`, `M4`, `L2`],
    [`L1`, `M1`, `L1`, `M1`],
    [`L2`, `L2`, `M4`, `L2`],
];

export const FREESPIN_BASE_REELSET: string[][] = [
    [`X`, `X`, `X`, `X`, `X`, `X`],
    [`X`, `X`, `X`, `X`, `X`, `X`],
    [`X`, `X`, `X`, `X`, `X`, `X`],
    [`X`, `X`, `X`, `X`, `X`, `X`],
    [`X`, `X`, `X`, `X`, `X`, `X`],
    [`X`, `X`, `X`, `X`, `X`, `X`],
];

export const symbolsArray: string[] = Object.values(ApocalypseSymbolNames);

export const symbolConfig: IApocalypseSymbolConfig = {
    symbolWidth: 105,
    symbolHeight: 105,
    symbolSpacing: 1.8,
};

export const normalConfig: IApocalypseReelAnimation = {
    startSymbolsDelay: 0.05,
    startEase: `circ.in`,
    startMinDelay: 0,
    startReelsDelay: 0.1,
    stopSpinReelDelay: 0.08,
    skipReelsDelay: 0.1,
    skipCurrentSymbolsTimeScale: 6,
    showNearWinAnimationDelay: 0,
    nearWinDuration: 1.1,
    hideNearWinDelay: 0.4,
    stepDuration: 0.04,
    stepSize: symbolConfig.symbolHeight + symbolConfig.symbolSpacing,
};

export const fsConfig: IApocalypseReelAnimation = {
    ...normalConfig,
    startReelsDelay: 0.02,
    stopSpinReelDelay: 0.04,
    skipReelsDelay: 0.02,
    skipCurrentSymbolsTimeScale: 20,
    stepDuration: 0.06,
    reelTeasingDuration: 1,
};

export const reelsConfig: IApocalypseReelConfig = {
    reelsNumber: 6,
    normalRowsNumber: 4,
    fsRowsNumber: 6,
    reelSpacing: 2,
    reelSpacingOffset: [0, 0, 0, 0, 0, 0],
    viewXPosition: 1,
    viewYPosition: 1,
    reelsContainerX: 0,
    reelsContainerY: 107,
    normalAnimation: normalConfig,
    stepDuration: 0.06,
    stepSize: symbolConfig.symbolHeight + symbolConfig.symbolSpacing,
    visualPaddingBefore: 1,
    paddingBefore: 0,
    visibleSymbols: 4,
    paddingAfter: 0,
    visualPaddingAfter: 1,
};

export const falseArray: boolean[][] = new Array(reelsConfig.reelsNumber).fill(
    new Array(reelsConfig.fsRowsNumber).fill(false)
);

// const salam = falseArray;

export const displayConfig: IApocalypseDisplayConfig = {
    gameWidth: 1280,
    gameHeight: 720,
    backgroundWidth: 1680,
    backgroundHeight: 1680,
    reelsViewWidth: 720,
    reelsViewHeight: 720,
};

export const normalReelSet = [
    ApocalypseSymbolNames.L1,
    ApocalypseSymbolNames.L2,
    ApocalypseSymbolNames.L3,
    ApocalypseSymbolNames.L4,
    ApocalypseSymbolNames.L5,
    ApocalypseSymbolNames.M1,
    ApocalypseSymbolNames.M2,
    ApocalypseSymbolNames.M3,
    ApocalypseSymbolNames.M4,
    ApocalypseSymbolNames.M5,
    ApocalypseSymbolNames.XNUDGE,
    ApocalypseSymbolNames.SCATTER,
];

export const fsStage1ReelSet = [
    ApocalypseSymbolNames.L1,
    ApocalypseSymbolNames.L2,
    ApocalypseSymbolNames.L3,
    ApocalypseSymbolNames.L4,
    ApocalypseSymbolNames.L5,

    ApocalypseSymbolNames.BLANK,
];

export const fsStage2ReelSet = [
    ApocalypseSymbolNames.M1,
    ApocalypseSymbolNames.M2,
    ApocalypseSymbolNames.M3,
    ApocalypseSymbolNames.M4,
    ApocalypseSymbolNames.M5,

    ApocalypseSymbolNames.BLANK,
];

export const lowSymbolFsValues = {
    [ApocalypseSymbolNames.L1]: 40,
    [ApocalypseSymbolNames.L2]: 20,
    [ApocalypseSymbolNames.L3]: 10,
    [ApocalypseSymbolNames.L4]: 5,
    [ApocalypseSymbolNames.L5]: 1,
};

export const winFontSize: number = 22;

export const fastSpinDelay: number = 0.55;
export const m5HighlightDelay: number = 0.35;
export const m4HighlightDelay: number = 0.35;
export const symbolLockDelay: number = 0.35;

export const devDebugConfig: IApocalypseDevDebugSettings = {
    disableReelMask: false,
    showReelDebugGraphics: false,
    showSymbolsDebugGraphics: false,
    showTimelineController: false,
    showSoundDebugger: false,
};

export const ApocalypseConfig: IApocalypseConfig = {
    reelConfig: reelsConfig,
    displayConfig: displayConfig,
    symbolConfig: symbolConfig,
    devDebugConfig: devDebugConfig,
    symbolsArray: symbolsArray,
};
